import { useState, useRef, useEffect } from 'react';
import { useDeviceType } from '../../../hooks/useDeviceType';
import { Logo } from './Logo';
import { DeleteLogoConfirmPopup } from '../../DeleteLogoConfirmPopup';

export function LogoList({
  logos,
  title,
  subTitle,
  handleAddNewLogo,
  activeLogoIndex,
  handleActiveLogoIndex,
  handleDeleteLogo,
  viewMode,
}) {
  const device = useDeviceType();
  const isCustomerMode = viewMode === 'customer';
  const fileInputRef = useRef(null);
  const [deletePopupState, setDeletePopupState] = useState({
    isOpen: false,
    logo: null,
    isLoading: false,
  });
  // State to force re-render when window.decals changes
  const [, setDecalsUpdate] = useState(0);

  const handleDeleteClick = (logo) => {
    setDeletePopupState({
      isOpen: true,
      logo: logo,
    });
  };

  const handleConfirmDelete = async () => {
    if (deletePopupState.logo && handleDeleteLogo) {
      setDeletePopupState((prev) => ({ ...prev, isLoading: true }));
      try {
        await handleDeleteLogo(deletePopupState.logo);
        setDeletePopupState({
          isOpen: false,
          logo: null,
          isLoading: false,
        });
      } catch (error) {
        setDeletePopupState((prev) => ({ ...prev, isLoading: false }));
      }
    }
  };

  const handleCancelDelete = () => {
    setDeletePopupState({
      isOpen: false,
      logo: null,
    });
  };

  const handleFileChange = (e) => {
    handleAddNewLogo(e);
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Monitor window.decals changes to trigger re-render when logos are added/removed from model
  useEffect(() => {
    let lastDecalsLength = window.decals?.length || 0;
    let lastDecalsIds =
      window.decals
        ?.map((d) => (d.logo && d.logo.Id ? d.logo.Id : null))
        .filter((id) => id !== null)
        .sort()
        .join(',') || '';

    const checkDecals = () => {
      const currentLength = window.decals?.length || 0;
      const currentIds =
        window.decals
          ?.map((d) => (d.logo && d.logo.Id ? d.logo.Id : null))
          .filter((id) => id !== null)
          .sort()
          .join(',') || '';

      // Only update state if decals actually changed (length or IDs)
      if (currentLength !== lastDecalsLength || currentIds !== lastDecalsIds) {
        lastDecalsLength = currentLength;
        lastDecalsIds = currentIds;
        setDecalsUpdate((prev) => prev + 1);
      }
    };

    // Check periodically for changes in window.decals
    // Increased interval to 200ms to reduce performance impact
    const interval = setInterval(checkDecals, 200);

    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <>
      <div
        style={{
          boxSizing: 'border-box',
          display: 'flex',
          gap: '12px',
          scrollbarWidth: 'none',
          overflowX: device === 'mobile' ? 'auto' : 'hidden',
          overflowY: device === 'mobile' ? 'hidden' : 'auto',
          flexDirection: device === 'mobile' ? 'row' : 'column',
          height: '100%',
        }}
      >
        {!isCustomerMode && (
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <div className="addLogo">
              <img src="/theme/img/plusIcon.svg" alt="plusIcon" />
              <input
                ref={fileInputRef}
                className="fullOwner_opacity"
                type="file"
                name="myfile"
                accept="image/*"
                style={{ width: '100%', height: '100%' }}
                onChange={handleFileChange}
              />
            </div>
          </div>
        )}

        {logos?.map((model, index) => {
          // Check if logo is currently on the model (exists in window.decals)
          const isLogoOnModel =
            window.decals &&
            window.decals.some(
              (d) =>
                d.logo &&
                d.logo.Id === model.Id &&
                d.logo.IsText !== true &&
                d.logo.IsPattern !== true,
            );

          // Hide logo if it's already on the model
          if (isLogoOnModel) {
            return null;
          }

          const isNew = model.Position === null;
          const isUserUploadedLogo = model.isUserUploadedLogo === true;
          return (
            <Logo
              key={model?.Id || index}
              scale={model.Scale}
              rotate={model.Rotate}
              image={model.Thumbnail}
              onClick={(ratio) => handleActiveLogoIndex(index, ratio)}
              active={activeLogoIndex === index}
              isNew={isNew}
              title={title}
              subTitle={subTitle}
              isUserUploadedLogo={isUserUploadedLogo}
              onDelete={() => handleDeleteClick(model)}
            />
          );
        })}
      </div>
      <DeleteLogoConfirmPopup
        isOpen={deletePopupState.isOpen}
        logo={deletePopupState.logo}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={deletePopupState.isLoading}
      />
    </>
  );
}
